import "./TasksGeneralView.css";

import { SortColumn } from "react-data-grid";

import { ClipboardListIcon, RoundedPlusSignIcon } from "../../assets";
import { SelectData } from "../../models";
import { GetTaskBoardResponse, SearchWorkflowTasksResponse } from "../../service/query";
import {
  Button,
  Column,
  CursorDataGrid,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  Row,
  TagFilter,
} from "../../widget";
import { WorkflowTaskPanel } from "../WorkflowTaskPanel";

export interface TasksGeneralViewProps {
  title?: string;
  noDataMessage?: string;
  variant?: "seed" | "default";
  showAddTask?: boolean;
  showTagFilter?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[];
  onChange: DataGridCursorDataLoadEventHandler;
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  hasData: boolean;
  refresh: boolean;
  handleRefresh: () => void;

  tagsFilterSelectData?: SelectData;
  tagsFilterValues?: string[];
  onTagsFilter?: (keys: string[]) => void;

  /*
    Task panel: empty when opened via "Add task", or carrying the row whose name was clicked. The panel
    reads the type off the task itself (no step ⇒ general), so the same overlay serves both kinds of row.
  */
  showWorkflowTaskPanel: boolean;
  selectedPanelTask?: SearchWorkflowTasksResponse["results"][0];
  handleAddTask: () => void;
  handleCloseWorkflowTaskPanel: () => void;
  /*
    The workflow whose object (project / deal / landholding) the created task hangs off. The panel
    resolves objectType/objectUuid from it, so without it a created general task is left unscoped and
    cannot appear under its parent.
  */
  workflow?: GetTaskBoardResponse["results"][number];
}

/*
  Shared combined workflow + general task grid. Presentational: all state and handlers arrive as props
  (usually from useTasksGeneralView) so the grid can be reused across the tasks board and the project /
  deal / landholding task tabs, and mocked directly in Storybook. "Show filters" and Previous/Next
  pagination are provided by CursorDataGrid itself.
*/
export const TasksGeneralView = ({
  title = "General tasks",
  noDataMessage = "No tasks to display",
  variant,
  showAddTask = true,
  showTagFilter = true,

  columns,
  onChange,
  defaultSortingCriteria,
  dataIsLoading,
  hasData,
  refresh,
  handleRefresh,

  tagsFilterSelectData,
  tagsFilterValues,
  onTagsFilter,

  showWorkflowTaskPanel,
  selectedPanelTask,
  handleAddTask,
  handleCloseWorkflowTaskPanel,
  workflow,
}: TasksGeneralViewProps): JSX.Element => {
  // When there are no tasks (and nothing loading), swap the grid + header actions for the empty state.
  const showEmpty = !dataIsLoading && !hasData;

  return (
    <div className="TasksGeneralView">
      <Row spacingV="m">
        <Column span={6}>
          <h2>{title}</h2>
        </Column>
        <Column span={6}>
          {!showEmpty && (
            <div className="TasksGeneralViewHeaderActions">
              {showTagFilter && onTagsFilter && (
                <TagFilter
                  tags={tagsFilterSelectData ?? []}
                  values={tagsFilterValues ?? []}
                  onChange={onTagsFilter}
                  corners={variant === "seed" ? "rounded" : "default"}
                />
              )}
              {showAddTask && (
                <Button
                  text="Add task"
                  size="small"
                  icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
                  iconPosition="right"
                  onClick={handleAddTask}
                />
              )}
            </div>
          )}
        </Column>
      </Row>

      {/* Grid stays mounted (hidden when empty) so it keeps fetching on refresh, e.g. after adding a task. */}
      <div className={showEmpty ? "TasksGeneralViewGridHidden" : undefined}>
        <CursorDataGrid
          columns={columns}
          clientSide={false}
          filterable
          onChange={onChange}
          data={[]}
          refresh={refresh}
          pagination={{ pageSize: 10 }}
          noDataMessage={noDataMessage}
          dataIsLoading={dataIsLoading}
          defaultSortingCriteria={defaultSortingCriteria}
          variant={variant}
        />
      </div>

      {showEmpty && (
        <div className="TasksGeneralViewEmpty">
          <div className="TasksGeneralViewEmptyBadge">
            <ClipboardListIcon />
          </div>
          <p className="TasksGeneralViewEmptyText">No tasks yet</p>
          {showAddTask && (
            <Button
              text="Add task"
              icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
              iconPosition="right"
              onClick={handleAddTask}
            />
          )}
        </div>
      )}

      {showWorkflowTaskPanel && (
        <WorkflowTaskPanel
          task={selectedPanelTask}
          currentStepUuid={selectedPanelTask?.step?.uuid}
          workflow={workflow}
          // Only used in create mode; on an opened task the type comes from the task's own step.
          isGeneralTask
          show={showWorkflowTaskPanel}
          refresh={refresh}
          handleRefresh={handleRefresh}
          onClose={handleCloseWorkflowTaskPanel}
        />
      )}
    </div>
  );
};
